/**
 * Role Play Sessions list — past practice sessions with score + date.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Loader2, Sparkles, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIFFICULTY_COLOR = {
    friendly: '#1DA063', standard: '#3B82F6',
    hard: '#F2A423', nightmare: '#E5484D',
};

function scoreColor(s) {
    if (s == null) return '#B8B8BE';
    if (s >= 85) return '#1DA063';
    if (s >= 70) return '#F2A423';
    return '#E5484D';
}

export default function RolePlaySessions() {
    const navigate = useNavigate();
    const { getAuthHeader } = useAuth();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API}/role-play/sessions`, {
                    headers: getAuthHeader(),
                    params: { limit: 50 },
                });
                setSessions(res.data?.sessions || []);
            } catch (e) {
                toast.error('Failed to load sessions');
            } finally {
                setLoading(false);
            }
        })();
    }, [getAuthHeader]);

    return (
        <div className="max-w-5xl mx-auto p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Role Play
                    </h1>
                    <p className="text-sm text-stone-500 mt-1">
                        Practice with AI avatars · {sessions.length} session{sessions.length !== 1 ? 's' : ''}
                    </p>
                </div>
                <button
                    onClick={() => navigate('/practice/role-play/new')}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-stone-900 text-white text-sm font-semibold hover:bg-stone-800"
                >
                    <Plus className="w-4 h-4" /> New session
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
                </div>
            ) : sessions.length === 0 ? (
                <div
                    className="rounded-2xl p-12 text-center text-white"
                    style={{ background: 'linear-gradient(135deg, #2d1a6e, #1a0f3d)' }}
                >
                    <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-70" />
                    <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: '"Instrument Serif", serif' }}>
                        Run your first role play.
                    </h2>
                    <p className="text-sm opacity-70 mb-5">
                        Pick one or more AI avatars, set a difficulty, and practice a playbook — just like a real call.
                    </p>
                    <button
                        onClick={() => navigate('/practice/role-play/new')}
                        className="px-5 py-2.5 rounded-lg bg-white text-stone-900 text-sm font-semibold"
                    >
                        ▶ Start role play
                    </button>
                </div>
            ) : (
                <div className="space-y-2">
                    {sessions.map(s => {
                        const d = new Date(s.started_at);
                        const diffColor = DIFFICULTY_COLOR[s.difficulty] || '#3B82F6';
                        const sc = scoreColor(s.overall_score);
                        return (
                            <button
                                key={s.session_id}
                                onClick={() => navigate(`/practice/role-play/sessions/${s.session_id}`)}
                                className="w-full text-left bg-white border border-stone-200 rounded-xl p-4 hover:border-stone-300 hover:shadow-sm transition-all"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-sm font-bold">
                                                {(s.avatar_ids || []).length} avatar{(s.avatar_ids || []).length !== 1 ? 's' : ''}
                                            </span>
                                            <span
                                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                                                style={{ background: diffColor + '22', color: diffColor }}
                                            >
                                                <span className="w-1 h-1 rounded-full" style={{ background: diffColor }} />
                                                {s.difficulty}
                                            </span>
                                            {s.status === 'active' && (
                                                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">
                                                    In progress
                                                </span>
                                            )}
                                            {s.status === 'processing' && (
                                                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700">
                                                    Scoring…
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3 text-xs text-stone-500">
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                {d.toLocaleDateString()} · {d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                            {s.duration_sec > 0 && (
                                                <span>{Math.round(s.duration_sec / 60)} min</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-2xl font-bold tracking-tight" style={{ color: sc }}>
                                            {s.overall_score == null ? '—' : s.overall_score}
                                        </div>
                                        <div className="text-[10px] text-stone-400 uppercase tracking-widest font-bold">
                                            Score
                                        </div>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
